import { gql, useQuery } from '@apollo/client';
import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { PUBLIC_ROUTES } from '~/client/components/public-routes';
import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';

// List of public route prefixes where we should skip user queries
const PUBLIC_ROUTE_PREFIXES = Object.values(PUBLIC_ROUTES).map((route) =>
  route.split(':')[0].replace(/\/$/, '')
);

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

// Only fetch organization project data on dashboard, project, and ai-system routes
const isAllowedRoute = (pathname: string): boolean =>
  pathname.startsWith('/dashboard') ||
  pathname.startsWith('/project') ||
  pathname.startsWith('/ai-system');

// Query to fetch organization project ID using dedicated resolver
const ORGANIZATION_PROJECT_ID_QUERY = gql`
  query OrganizationProjectId {
    organizationProjectId
  }
`;

interface OrganizationProjectContextType {
  /**
   * The ID of the organization project for the current organization
   * null if no organization project exists
   */
  organizationProjectId: string | null;
  /**
   * The full organization project data (cached)
   * null if no organization project exists or still loading
   */
  organizationProject: GQL.ProjectFragment | null;
  /**
   * Whether the organization project data is still loading
   */
  loading: boolean;
  /**
   * Whether the full project data is loading (after ID is fetched)
   */
  projectLoading: boolean;
  /**
   * Check if a given project ID is the organization project
   */
  isOrganizationProjectId: (projectId: string | undefined) => boolean;
  /**
   * Refetch the organization project data
   */
  refetch: () => Promise<any>;
  /**
   * Refetch just the project data
   */
  refetchProject: () => Promise<any>;
}

const OrganizationProjectContext =
  createContext<OrganizationProjectContextType>({
    organizationProjectId: null,
    organizationProject: null,
    loading: true,
    projectLoading: true,
    isOrganizationProjectId: () => false,
    refetch: () => Promise.resolve(),
    refetchProject: () => Promise.resolve(),
  });

interface Props {
  children: React.ReactNode;
}

export const OrganizationProjectProvider: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);

  // Skip user profile fetching on public routes to prevent 401 errors
  const { userProfile, isLoading: isUserProfileLoading } = useUserProfile({
    skip: isPublic,
  });

  const shouldFetch = isAllowedRoute(location.pathname);

  // Step 1: Fetch organization project ID
  const { data, loading, refetch } = useQuery<{
    organizationProjectId: string | null;
  }>(ORGANIZATION_PROJECT_ID_QUERY, {
    skip: !userProfile?.defaultOrganizationId || !shouldFetch,
    fetchPolicy: 'cache-and-network', // Use cache but also refetch to catch newly created projects
  });

  const organizationProjectId = data?.organizationProjectId ?? null;

  // Step 2: Fetch full organization project data once we have the ID
  const {
    data: projectData,
    loading: projectLoading,
    refetch: refetchProject,
  } = GQL.useProjectFromIdQuery({
    variables: { id: organizationProjectId ?? '' },
    skip: !organizationProjectId || !shouldFetch,
    fetchPolicy: 'cache-first', // Cache the organization project data
  });

  // Filter out archived assessments (same as DashboardDataProvider does)
  const organizationProject = useMemo(() => {
    if (!projectData?.projectFromId) {
      return null;
    }
    return {
      ...projectData.projectFromId,
      assessments: projectData.projectFromId.assessments.filter(
        (assessment) => assessment.status !== GQL.AssessmentStatusType.Archived
      ),
    };
  }, [projectData]);

  const isOrganizationProjectId = useMemo(() => {
    return (projectId: string | undefined): boolean => {
      if (!projectId || !organizationProjectId) {
        return false;
      }
      return projectId === organizationProjectId;
    };
  }, [organizationProjectId]);

  const value = useMemo(
    () => ({
      organizationProjectId,
      organizationProject,
      // Only show loading while queries are in flight; avoid staying loading when
      // OrganizationProjectId query fails or returns null (so org page can show setup UI).
      loading: shouldFetch ? isUserProfileLoading || loading : false,
      projectLoading: shouldFetch ? projectLoading : false,
      isOrganizationProjectId,
      refetch,
      refetchProject,
    }),
    [
      organizationProjectId,
      organizationProject,
      isUserProfileLoading,
      loading,
      projectLoading,
      shouldFetch,
      isOrganizationProjectId,
      refetch,
      refetchProject,
    ]
  );

  return (
    <OrganizationProjectContext.Provider value={value}>
      {children}
    </OrganizationProjectContext.Provider>
  );
};

/**
 * Hook to access organization project context
 * Provides organizationProjectId and utility functions
 */
export const useOrganizationProjectContext = () => {
  const context = useContext(OrganizationProjectContext);
  if (!context) {
    throw new Error(
      'useOrganizationProjectContext must be used within OrganizationProjectProvider'
    );
  }
  return context;
};

/**
 * Utility hook to check if a project is an organization type
 * Uses the project data from DashboardDataContext or passed project
 */
export const useIsOrganizationProject = (
  project: { projectType?: GQL.ProjectType } | undefined | null
): boolean => {
  return project?.projectType === GQL.ProjectType.Organization;
};

export default OrganizationProjectContext;
